/**
 * The vessel: a brain suspended in a glass cylinder.
 *
 * Procedural, no sprites. Render order is cables, glass body, fluid, glow,
 * brain, sulci, cracks, bubbles, glass front, electrode tips. Nothing else in
 * the app touches canvas.
 */

import { CRACK_HEAT } from '../data/balance.ts';
import { clockMult, thermalBonus } from '../sim/formulas.ts';
import { ART_LAYOUT, loadVesselArt, type VesselArt } from './vesselArt.ts';
import {
  createNeuralField,
  drawBrainPulse,
  drawBurst,
  drawNeural,
  pulseNeural,
  resetNeuralField,
  updateNeural,
  type NeuralField,
  type NeuralInput,
} from './neural.ts';
import {
  clearBubbles,
  createBubbleField,
  drawBubbles,
  updateBubbles,
  type BubbleField,
} from './particles.ts';

type RGB = readonly [number, number, number];

const TISSUE: RGB = [232, 118, 143];
const TISSUE_HOT: RGB = [255, 77, 94];

/** ~14 points on a circle, each perturbed by layered sine waves. */
const LOBE_POINTS = 14;

interface Crack {
  points: Array<{ a: number; r: number }>;
  width: number;
}

/**
 * One fold, confined to a single hemisphere. Generated once and kept, so the
 * brain has a consistent surface rather than reshuffling every frame.
 */
interface Sulcus {
  /** -1 left hemisphere, +1 right. */
  side: number;
  /** Vertical position as a fraction of the radius. */
  y: number;
  /** How far out from the fissure the fold starts and ends. */
  inner: number;
  outer: number;
  /** Curvature and phase of the wander. */
  bow: number;
  phase: number;
}

export interface VesselView {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  bubbles: BubbleField;
  cracks: Crack[];
  sulci: Sulcus[];
  /** Nodes and sparks drawn inside the brain. */
  neural: NeuralField;
  /** Optional hand-supplied art. When present it replaces the whole vessel. */
  art: VesselArt;
  /** Drain progress during the meltdown sequence, 0..1. */
  drain: number;
  width: number;
  height: number;
  dpr: number;
}

export function createVessel(
  canvas: HTMLCanvasElement,
  onArtReady?: (art: VesselArt) => void,
): VesselView {
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('Canvas 2D context unavailable');
  return {
    canvas,
    ctx,
    bubbles: createBubbleField(),
    cracks: [],
    sulci: makeSulci(),
    neural: createNeuralField(),
    art: loadVesselArt(onArtReady),
    drain: 0,
    width: 0,
    height: 0,
    dpr: 1,
  };
}

/** A fixed set of folds: five or six per hemisphere, staggered and jittered. */
function makeSulci(): Sulcus[] {
  const out: Sulcus[] = [];
  for (const side of [-1, 1]) {
    const count = 6;
    for (let i = 0; i < count; i++) {
      const t = (i + 0.5) / count;
      out.push({
        side,
        y: (t - 0.5) * 1.5 + (Math.random() - 0.5) * 0.12,
        inner: 0.1 + Math.random() * 0.14,
        outer: 0.72 + Math.random() * 0.26,
        bow: (Math.random() - 0.5) * 0.5 + (t < 0.5 ? 0.28 : -0.28),
        phase: Math.random() * Math.PI * 2,
      });
    }
  }
  return out;
}

/** Clears the damage from the previous run. Called on meltdown. */
/** Fires a burst of sparks. Called when the player buys something. */
export function pulseVessel(view: VesselView, count = 6): void {
  pulseNeural(view.neural, count);
}

export function resetVessel(view: VesselView): void {
  view.cracks.length = 0;
  resetNeuralField(view.neural);
  view.drain = 0;
  clearBubbles(view.bubbles);
}

function resize(view: VesselView): void {
  const dpr = Math.min(globalThis.devicePixelRatio ?? 1, 2);
  const rect = view.canvas.getBoundingClientRect();
  const w = Math.max(1, Math.round(rect.width));
  const h = Math.max(1, Math.round(rect.height));
  if (view.width === w && view.height === h && view.dpr === dpr) return;

  view.width = w;
  view.height = h;
  view.dpr = dpr;
  view.canvas.width = Math.round(w * dpr);
  view.canvas.height = Math.round(h * dpr);
}

export interface VesselInput {
  heat: number;
  /** Sparks per second, driving how busy the neural field looks. */
  sparkRate: number;
  /** Generators owned, which sets how many nodes the field shows. */
  totalGenerators: number;
  /** 0..1, how much ground the intrusive thoughts have taken. */
  voidShare: number;
  peakHeat: number;
  clock: number;
  clockExp: number;
  timeSec: number;
  dt: number;
  reducedMotion: boolean;
}

export function drawVessel(view: VesselView, input: VesselInput): void {
  resize(view);
  const { ctx, width: w, height: h } = view;

  ctx.setTransform(view.dpr, 0, 0, view.dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  if (view.art.ready && view.art.image !== null) {
    drawArtVessel(view, view.art.image, input);
    return;
  }

  // A tall, narrow cylinder standing on the panel floor.
  const cx = w / 2;
  const cylW = Math.min(w * 0.44, h * 0.62);
  const top = h * 0.1;
  const bottom = h * 0.93;
  const left = cx - cylW / 2;
  const right = cx + cylW / 2;
  const rimRy = cylW * 0.16;

  const brainR = cylW * 0.4;
  const brainY = top + (bottom - top) * 0.44;

  drawCables(ctx, w, left, right, top, brainY);
  drawGlassBody(ctx, left, right, top, bottom, rimRy);

  const fluidTop = top + rimRy * 1.5 + (bottom - top) * view.drain;
  if (view.drain < 1) {
    drawFluid(ctx, left, right, fluidTop, bottom, rimRy, input, view.drain);
  }

  if (view.drain < 0.95) {
    drawGlow(ctx, cx, brainY, brainR, input);
    drawBrain(ctx, view, cx, brainY, brainR, input);
    syncCracks(view, input.peakHeat);
    drawCracks(ctx, view, cx, brainY, brainR);
  }

  if (!input.reducedMotion && view.drain === 0) {
    updateBubbles(view.bubbles, input.dt, input.heat, left + 8, right - 8, fluidTop, bottom - 6);
    drawBubbles(ctx, view.bubbles);
  }

  if (view.drain < 0.95) {
    runNeural(view, ctx, cx, brainY, brainR * 0.95, brainR * 0.8, input);
  }

  drawGlassFront(ctx, left, right, top, bottom, rimRy);
  drawElectrodeTips(ctx, cx, left, right, top, brainY);
  drawVoid(ctx, w, h, input);
}

/** Advances and draws the neural field over whichever brain is on screen. */
function runNeural(
  view: VesselView,
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  input: VesselInput,
): void {
  const neural: NeuralInput = {
    totalGenerators: input.totalGenerators,
    sparkRate: input.sparkRate,
    dt: input.dt,
    timeSec: input.timeSec,
    reducedMotion: input.reducedMotion,
  };
  updateNeural(view.neural, neural);
  drawBrainPulse(ctx, cx, cy, rx, ry, neural);
  drawNeural(ctx, view.neural, cx, cy, rx, ry, neural);
  drawBurst(ctx, view.neural, cx, cy, rx, ry);
}

/**
 * The void, closing in from the edges.
 *
 * Drawn last and over everything, because it is meant to read as the room
 * itself going dark rather than as something inside the jar.
 */
function drawVoid(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  input: VesselInput,
): void {
  const share = Math.max(0, Math.min(1, input.voidShare));
  if (share <= 0.01) return;

  const grad = ctx.createRadialGradient(
    w / 2,
    h / 2,
    Math.max(1, Math.min(w, h) * (0.55 - share * 0.48)),
    w / 2,
    h / 2,
    Math.max(w, h) * 0.68,
  );
  grad.addColorStop(0, 'rgba(2, 3, 6, 0)');
  grad.addColorStop(0.55, `rgba(3, 3, 8, ${(share * 0.5).toFixed(3)})`);
  grad.addColorStop(1, `rgba(2, 2, 6, ${(0.55 + share * 0.45).toFixed(3)})`);

  ctx.save();
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // Tendrils, reaching in as soon as it starts gaining rather than only at the
  // halfway mark. A vignette alone was too easy to mistake for the panel art.
  if (share > 0.15 && !input.reducedMotion) {
    const strength = (share - 0.15) / 0.85;
    ctx.strokeStyle = `rgba(6, 2, 10, ${(0.28 + 0.55 * strength).toFixed(3)})`;
    ctx.lineCap = 'round';
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * Math.PI * 2 + input.timeSec * 0.05;
      const reach = Math.min(w, h) * (0.5 - strength * 0.34);
      const sway = Math.sin(input.timeSec * 0.8 + i) * 0.22;
      ctx.lineWidth = 3 + strength * 10;
      ctx.beginPath();
      ctx.moveTo(w / 2 + Math.cos(a) * w * 0.6, h / 2 + Math.sin(a) * h * 0.6);
      ctx.quadraticCurveTo(
        w / 2 + Math.cos(a + sway) * reach * 1.7,
        h / 2 + Math.sin(a + sway) * reach * 1.7,
        w / 2 + Math.cos(a + sway) * reach,
        h / 2 + Math.sin(a + sway) * reach,
      );
      ctx.stroke();
    }
  }
  ctx.restore();
}


// ---------------------------------------------------------------------------
// Art path - the supplied image is the vessel; everything live goes on top
// ---------------------------------------------------------------------------

/** Cover-fits the art and composites the reactive layers over it. */
function drawArtVessel(view: VesselView, image: HTMLImageElement, input: VesselInput): void {
  const { ctx, width: w, height: h } = view;

  // Cover-fit: fill the panel without distorting the art.
  const scale = Math.max(w / image.width, h / image.height);
  const dw = image.width * scale;
  const dh = image.height * scale;
  const dx = (w - dw) / 2;
  const dy = (h - dh) / 2;

  const at = (fx: number, fy: number): [number, number] => [dx + dw * fx, dy + dh * fy];
  const brain = ART_LAYOUT.brain;
  const [bx, by] = at(brain.cx, brain.cy);
  const brx = dw * brain.rx;
  const bry = dh * brain.ry;

  ctx.drawImage(image, dx, dy, dw, dh);

  const rgb = tissue(input.heat);
  const heat = Math.min(1, Math.max(0, input.heat / 100));

  // Heat tint, confined to the tissue and feathered so it has no hard edge.
  if (heat > 0.02 && view.drain < 0.95) {
    const tint = ctx.createRadialGradient(bx, by, brx * 0.1, bx, by, brx * 1.15);
    tint.addColorStop(0, shade(rgb, 1, 0.72 * heat));
    tint.addColorStop(0.7, shade(rgb, 1, 0.4 * heat));
    tint.addColorStop(1, shade(rgb, 1, 0));

    ctx.save();
    ctx.globalCompositeOperation = 'overlay';
    ctx.beginPath();
    ctx.ellipse(bx, by, brx * 1.2, bry * 1.2, 0, 0, Math.PI * 2);
    ctx.fillStyle = tint;
    ctx.fill();
    ctx.restore();
  }

  // Clock glow, added rather than blended, so the rig visibly runs hotter.
  const mult = clockMult(input.clock, input.clockExp);
  const strength = Math.min(1, Math.max(0, (mult - 0.8) / 4.2));
  if (strength > 0.01 && view.drain < 0.95) {
    const radius = Math.max(brx, bry) * (1.4 + strength * 1.4);
    const glow = ctx.createRadialGradient(bx, by, brx * 0.2, bx, by, radius);
    glow.addColorStop(0, shade(rgb, 1, 0.4 * strength));
    glow.addColorStop(1, shade(rgb, 1, 0));

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.beginPath();
    ctx.arc(bx, by, radius, 0, Math.PI * 2);
    ctx.fillStyle = glow;
    ctx.fill();
    ctx.restore();
  }

  // Damage, clipped to the tissue.
  if (view.drain < 0.95) {
    syncCracks(view, input.peakHeat);
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(bx, by, brx, bry, 0, 0, Math.PI * 2);
    ctx.clip();
    drawCracks(ctx, view, bx, by, Math.max(brx, bry) * 0.92);
    ctx.restore();
  }

  // Bubbles inside the fluid column.
  if (!input.reducedMotion && view.drain === 0) {
    const f = ART_LAYOUT.fluid;
    const [fx0, fy0] = at(f.x0, f.y0);
    const [fx1, fy1] = at(f.x1, f.y1);
    updateBubbles(view.bubbles, input.dt, input.heat, fx0, fx1, fy0, fy1);
    drawBubbles(ctx, view.bubbles);
  }

  if (view.drain < 0.95) {
    runNeural(view, ctx, bx, by, brx * 0.95, bry * 0.95, input);
  }

  // The art carries three readout labels. Paint over them and put real
  // numbers in their place, so the panel never states something untrue.
  drawArtLabels(ctx, dx, dy, dw, dh, input);
  drawVoid(ctx, w, h, input);

  // Meltdown: the fluid drains and the light goes out of it.
  if (view.drain > 0) {
    ctx.save();
    ctx.fillStyle = `rgba(8, 12, 12, ${Math.min(1, view.drain)})`;
    ctx.fillRect(dx, dy, dw, dh);
    ctx.restore();
  }
}

function drawArtLabels(
  ctx: CanvasRenderingContext2D,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
  input: VesselInput,
): void {
  const { x, ys, size } = ART_LAYOUT.labels;
  const px = dx + dw * x;
  const fontPx = Math.max(9, dh * size * 0.62);

  const bonus = thermalBonus(input.heat);
  const status = input.heat >= 85 ? '[CRITICAL]' : input.heat >= 60 ? '[HOT]' : '[OK]';
  const lines = [
    `SYNAPTIC COHERENCE: ${(100 - input.heat * 0.42).toFixed(1)}%`,
    `THERMAL YIELD ${status}  +${((bonus - 1) * 100).toFixed(0)}%`,
    `CLOCK INDEX: ${input.clock}%`,
  ];

  ctx.save();

  // Cover the baked-in text with the plate colour behind it.
  const top = dy + dh * (ys[0]! - size);
  const bottom = dy + dh * (ys[ys.length - 1]! + size * 0.6);
  const scrim = ctx.createLinearGradient(px - dw * 0.02, 0, px + dw * 0.02, 0);
  scrim.addColorStop(0, 'rgba(58, 72, 70, 0)');
  scrim.addColorStop(1, 'rgba(58, 72, 70, 1)');
  ctx.fillStyle = scrim;
  ctx.fillRect(px - dw * 0.02, top, dw * 0.04, bottom - top);
  ctx.fillStyle = 'rgb(58, 72, 70)';
  ctx.fillRect(px + dw * 0.02, top, dx + dw - (px + dw * 0.02), bottom - top);

  ctx.font = `600 ${fontPx.toFixed(1)}px "IBM Plex Mono", ui-monospace, monospace`;
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = 'rgba(214, 232, 228, 0.92)';
  lines.forEach((line, i) => {
    const y = dy + dh * (ys[i] ?? 0);
    ctx.fillText(line, px, y);
  });

  ctx.restore();
}

// ---------------------------------------------------------------------------
// Colour
// ---------------------------------------------------------------------------

/** Tissue colour at a given heat, lerped from at-rest to redline. */
function tissue(heat: number): RGB {
  const t = Math.min(1, Math.max(0, heat / 100));
  return [
    TISSUE[0] + (TISSUE_HOT[0] - TISSUE[0]) * t,
    TISSUE[1] + (TISSUE_HOT[1] - TISSUE[1]) * t,
    TISSUE[2] + (TISSUE_HOT[2] - TISSUE[2]) * t,
  ];
}

/**
 * Scales a colour toward black (amount < 1) or white (amount > 1).
 *
 * Sulci and outlines must darken the actual tissue colour. Lerping further
 * along the heat ramp instead would make them vanish at heat 0, where the
 * ramp has not started.
 */
function shade(rgb: RGB, amount: number, alpha = 1): string {
  const mix = (c: number): number =>
    Math.round(amount <= 1 ? c * amount : c + (255 - c) * (amount - 1));
  return `rgba(${mix(rgb[0])}, ${mix(rgb[1])}, ${mix(rgb[2])}, ${alpha})`;
}

// ---------------------------------------------------------------------------
// Glass
// ---------------------------------------------------------------------------

function drawGlassBody(
  ctx: CanvasRenderingContext2D,
  left: number,
  right: number,
  top: number,
  bottom: number,
  rimRy: number,
): void {
  const cx = (left + right) / 2;
  const rx = (right - left) / 2;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(left, top);
  ctx.lineTo(left, bottom);
  ctx.ellipse(cx, bottom, rx, rimRy, 0, Math.PI, 0, true);
  ctx.lineTo(right, top);
  ctx.closePath();
  ctx.fillStyle = 'rgba(220, 232, 230, 0.14)';
  ctx.fill();
  ctx.restore();
}

function drawGlassFront(
  ctx: CanvasRenderingContext2D,
  left: number,
  right: number,
  top: number,
  bottom: number,
  rimRy: number,
): void {
  const cx = (left + right) / 2;
  const rx = (right - left) / 2;
  const w = right - left;

  ctx.save();

  // Two vertical highlight bands.
  ctx.fillStyle = 'rgba(255, 255, 255, 0.11)';
  ctx.fillRect(left + w * 0.11, top + rimRy, w * 0.06, bottom - top - rimRy);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.06)';
  ctx.fillRect(left + w * 0.79, top + rimRy, w * 0.035, bottom - top - rimRy);

  ctx.strokeStyle = 'rgba(226, 238, 235, 0.45)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.ellipse(cx, bottom, rx, rimRy, 0, 0, Math.PI);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(left, top);
  ctx.lineTo(left, bottom);
  ctx.moveTo(right, top);
  ctx.lineTo(right, bottom);
  ctx.stroke();

  // A steel collar around the top rim.
  ctx.beginPath();
  ctx.ellipse(cx, top, rx, rimRy, 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(150, 160, 156, 0.55)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(226, 238, 235, 0.75)';
  ctx.lineWidth = 2.5;
  ctx.stroke();

  ctx.beginPath();
  ctx.ellipse(cx, top, rx * 0.82, rimRy * 0.8, 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(30, 40, 38, 0.3)';
  ctx.fill();

  ctx.restore();
}

function drawFluid(
  ctx: CanvasRenderingContext2D,
  left: number,
  right: number,
  fluidTop: number,
  bottom: number,
  rimRy: number,
  input: VesselInput,
  drain: number,
): void {
  const cx = (left + right) / 2;
  const rx = (right - left) / 2;
  const heat = input.heat / 100;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(left, fluidTop);
  ctx.lineTo(left, bottom);
  ctx.ellipse(cx, bottom, rx, rimRy, 0, Math.PI, 0, true);
  ctx.lineTo(right, fluidTop);
  ctx.closePath();

  const grad = ctx.createLinearGradient(0, fluidTop, 0, bottom);
  grad.addColorStop(0, `rgba(158, 204, 196, ${0.18 + heat * 0.12})`);
  grad.addColorStop(1, `rgba(88, 140, 134, ${0.3 + heat * 0.2})`);
  ctx.fillStyle = grad;
  ctx.fill();

  const wobble = input.reducedMotion ? 0 : Math.sin(input.timeSec * 1.6) * 1.6 * (1 - drain);
  ctx.beginPath();
  ctx.ellipse(cx, fluidTop + wobble, rx, rimRy * 0.9, 0, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(186, 224, 216, ${0.3 + heat * 0.16})`;
  ctx.fill();
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Brain
// ---------------------------------------------------------------------------

/**
 * Radius of the blob at a given angle.
 *
 * Two hemispheres with a cleft at the top and a flattened underside, plus a
 * slow breathe. Angles run clockwise from the right in canvas space, so the
 * top of the brain is at 1.5*PI.
 */
function lobeRadius(angle: number, r: number, t: number, reducedMotion: boolean): number {
  const top = Math.PI * 1.5;

  // The interhemispheric cleft: a notch at the crown.
  let d = Math.abs(((angle - top + Math.PI) % (Math.PI * 2)) - Math.PI);
  const cleft = 1 - 0.19 * Math.exp(-Math.pow(d * 3.4, 2));

  // Two lobes either side of it, and a flatter base.
  const lobes = 1 + 0.13 * Math.cos((angle - top) * 2) - 0.05 * Math.cos(angle - top);
  const wrinkle = 0.045 * Math.sin(angle * 5 + 1.2) + 0.03 * Math.sin(angle * 8 - 0.4);

  const breathe = reducedMotion
    ? 1
    : 1 + 0.02 * Math.sin(t * 1.1 + angle * 2) + 0.012 * Math.sin(t * 1.9 + angle * 3.3);

  return r * (lobes + wrinkle) * cleft * breathe;
}

function brainPath(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  t: number,
  reducedMotion: boolean,
): void {
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < LOBE_POINTS; i++) {
    const a = (i / LOBE_POINTS) * Math.PI * 2;
    const rr = lobeRadius(a, r, t, reducedMotion);
    pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr * 0.82]);
  }

  ctx.beginPath();
  const last = pts[LOBE_POINTS - 1]!;
  const first = pts[0]!;
  ctx.moveTo((last[0] + first[0]) / 2, (last[1] + first[1]) / 2);
  for (let i = 0; i < LOBE_POINTS; i++) {
    const cur = pts[i]!;
    const next = pts[(i + 1) % LOBE_POINTS]!;
    ctx.quadraticCurveTo(cur[0], cur[1], (cur[0] + next[0]) / 2, (cur[1] + next[1]) / 2);
  }
  ctx.closePath();
}

function drawGlow(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  input: VesselInput,
): void {
  const mult = clockMult(input.clock, input.clockExp);
  const strength = Math.min(1, Math.max(0, (mult - 0.8) / 4.2));
  if (strength <= 0.01) return;

  const rgb = tissue(input.heat);
  const radius = r * (1.6 + strength * 1.6);
  const grad = ctx.createRadialGradient(cx, cy, r * 0.4, cx, cy, radius);
  grad.addColorStop(0, shade(rgb, 1, 0.4 * strength));
  grad.addColorStop(1, shade(rgb, 1, 0));

  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.restore();
}

function drawBrain(
  ctx: CanvasRenderingContext2D,
  view: VesselView,
  cx: number,
  cy: number,
  r: number,
  input: VesselInput,
): void {
  const t = input.timeSec;
  const rgb = tissue(input.heat);

  ctx.save();
  brainPath(ctx, cx, cy, r, t, input.reducedMotion);

  const grad = ctx.createRadialGradient(cx - r * 0.32, cy - r * 0.4, r * 0.1, cx, cy, r * 1.3);
  grad.addColorStop(0, shade(rgb, 1.22));
  grad.addColorStop(0.55, shade(rgb, 1));
  grad.addColorStop(1, shade(rgb, 0.78));
  ctx.fillStyle = grad;
  ctx.fill();

  ctx.strokeStyle = shade(rgb, 0.6, 0.85);
  ctx.lineWidth = 1.6;
  ctx.stroke();

  // Folds, clipped to the blob so they never spill onto the fluid.
  ctx.clip();
  ctx.lineCap = 'round';

  const groove = Math.max(1.4, r * 0.055);
  for (const sulcus of view.sulci) {
    const drift = input.reducedMotion ? 0 : Math.sin(t * 0.7 + sulcus.phase) * r * 0.018;
    const y = cy + sulcus.y * r * 0.62 + drift;
    const x0 = cx + sulcus.side * r * sulcus.inner;
    const x1 = cx + sulcus.side * r * sulcus.outer;
    const bow = sulcus.bow * r;

    // A dark groove with a lighter lip just below it reads as depth.
    ctx.strokeStyle = shade(rgb, 0.6, 0.92);
    ctx.lineWidth = groove;
    ctx.beginPath();
    ctx.moveTo(x0, y);
    ctx.bezierCurveTo(
      x0 + (x1 - x0) * 0.3,
      y + bow,
      x0 + (x1 - x0) * 0.7,
      y - bow,
      x1,
      y + bow * 0.35,
    );
    ctx.stroke();

    ctx.strokeStyle = shade(rgb, 1.24, 0.4);
    ctx.lineWidth = groove * 0.42;
    ctx.beginPath();
    ctx.moveTo(x0, y + groove * 0.62);
    ctx.bezierCurveTo(
      x0 + (x1 - x0) * 0.3,
      y + bow + groove * 0.62,
      x0 + (x1 - x0) * 0.7,
      y - bow + groove * 0.62,
      x1,
      y + bow * 0.35 + groove * 0.62,
    );
    ctx.stroke();
  }

  // The central fissure, dividing the hemispheres.
  ctx.strokeStyle = shade(rgb, 0.44, 0.92);
  ctx.lineWidth = Math.max(2, r * 0.07);
  ctx.beginPath();
  ctx.moveTo(cx + r * 0.02, cy - r * 1.05);
  ctx.quadraticCurveTo(cx + r * 0.12, cy, cx - r * 0.02, cy + r * 0.98);
  ctx.stroke();

  // Cerebellum: a denser, finer-grained lobe tucked under the right rear.
  const ceX = cx + r * 0.44;
  const ceY = cy + r * 0.6;
  const ceRx = r * 0.42;
  const ceRy = r * 0.26;
  ctx.beginPath();
  ctx.ellipse(ceX, ceY, ceRx, ceRy, -0.18, 0, Math.PI * 2);
  ctx.fillStyle = shade(rgb, 0.84);
  ctx.fill();
  ctx.strokeStyle = shade(rgb, 0.58, 0.8);
  ctx.lineWidth = 1.2;
  ctx.stroke();

  ctx.save();
  ctx.clip();
  ctx.strokeStyle = shade(rgb, 0.62, 0.75);
  ctx.lineWidth = Math.max(0.8, r * 0.022);
  for (let i = -4; i <= 4; i++) {
    const y = ceY + i * ceRy * 0.24;
    ctx.beginPath();
    ctx.moveTo(ceX - ceRx, y + ceRx * 0.1);
    ctx.quadraticCurveTo(ceX, y - ceRy * 0.18, ceX + ceRx, y + ceRx * 0.1);
    ctx.stroke();
  }
  ctx.restore();

  // Brain stem, dropping out of the underside.
  ctx.restore();
  ctx.save();
  ctx.strokeStyle = shade(rgb, 0.8);
  ctx.lineWidth = r * 0.17;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(cx + r * 0.02, cy + r * 0.58);
  ctx.quadraticCurveTo(cx + r * 0.1, cy + r * 0.86, cx + r * 0.04, cy + r * 1.02);
  ctx.stroke();
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Cracks - damage reads as history, so they follow peak heat, not current heat
// ---------------------------------------------------------------------------

function syncCracks(view: VesselView, peakHeat: number): void {
  if (peakHeat <= CRACK_HEAT) return;

  const target = Math.min(16, Math.floor((peakHeat - CRACK_HEAT) / 2.5));
  while (view.cracks.length < target) {
    const points: Array<{ a: number; r: number }> = [];
    let angle = Math.random() * Math.PI * 2;
    let radius = 0.1 + Math.random() * 0.28;
    const steps = 3 + Math.floor(Math.random() * 3);
    for (let i = 0; i <= steps; i++) {
      points.push({ a: angle, r: radius });
      angle += (Math.random() - 0.5) * 1.0;
      radius += 0.1 + Math.random() * 0.18;
    }
    view.cracks.push({ points, width: 0.9 + Math.random() * 1.1 });
  }
}

function drawCracks(
  ctx: CanvasRenderingContext2D,
  view: VesselView,
  cx: number,
  cy: number,
  r: number,
): void {
  if (view.cracks.length === 0) return;

  ctx.save();
  // Kept subtle: over detailed artwork a hard black line reads as a scribble
  // rather than as damage.
  ctx.strokeStyle = 'rgba(32, 6, 12, 0.42)';
  ctx.lineCap = 'round';
  for (const crack of view.cracks) {
    ctx.lineWidth = crack.width * 0.8;
    ctx.beginPath();
    crack.points.forEach((p, i) => {
      const rr = Math.min(p.r, 0.94) * r;
      const x = cx + Math.cos(p.a) * rr;
      const y = cy + Math.sin(p.a) * rr * 0.82;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
  }
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Electrodes - static, entering from the chassis
// ---------------------------------------------------------------------------

/**
 * Cables run in from the panel edges. Widths are in CSS pixels: the context is
 * already scaled by devicePixelRatio, so canvas.width would be twice too far.
 */
function drawCables(
  ctx: CanvasRenderingContext2D,
  w: number,
  left: number,
  right: number,
  top: number,
  brainY: number,
): void {
  const runs: Array<[number, number, number, number]> = [
    [0, brainY - 40, left + 6, brainY - 14],
    [w, brainY + 30, right - 6, brainY + 6],
  ];

  ctx.save();
  ctx.lineCap = 'round';
  for (const [x0, y0, x1, y1] of runs) {
    const midX = (x0 + x1) / 2;

    ctx.strokeStyle = '#121817';
    ctx.lineWidth = 7;
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.quadraticCurveTo(midX, y0 + 26, x1, y1);
    ctx.stroke();

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.16)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x0, y0 - 1.6);
    ctx.quadraticCurveTo(midX, y0 + 24, x1, y1 - 1.6);
    ctx.stroke();
  }

  // A third cable down the spine of the rig, into the collar.
  const cx = (left + right) / 2;
  ctx.strokeStyle = '#121817';
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.moveTo(cx, 0);
  ctx.lineTo(cx, top);
  ctx.stroke();
  ctx.restore();
}

function drawElectrodeTips(
  ctx: CanvasRenderingContext2D,
  cx: number,
  left: number,
  right: number,
  top: number,
  brainY: number,
): void {
  const tips: Array<[number, number]> = [
    [left + 6, brainY - 14],
    [right - 6, brainY + 6],
    [cx, top + 2],
  ];

  ctx.save();
  for (const [x, y] of tips) {
    ctx.fillStyle = '#9aa29e';
    ctx.fillRect(x - 4, y - 4, 8, 8);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.fillRect(x - 4, y - 4, 8, 2.5);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.45)';
    ctx.lineWidth = 1;
    ctx.strokeRect(x - 4, y - 4, 8, 8);
  }
  ctx.restore();
}
